import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  AppWindow,
  Check,
  Copy,
  KeyRound,
  Loader2,
  Plus,
  RotateCw,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHead, Panel } from "@/components/bits";
import { cn } from "@/lib/utils";
import { publicApi } from "@/services/api-client";
import { BusinessGate } from "@/features/business/business-gate";
import { useI18n } from "@/i18n/i18n-context";
import {
  ConfirmDialog,
  Empty,
  ErrorBox,
  KV,
  SecretOnce,
  SkeletonRows,
  Tag,
  fmtWhen,
  record,
  rows,
  text,
  useServer,
  type ApiRecord,
} from "@/features/finance/kit";
import { WebhooksPanel } from "./webhooks-panel";

export default function ApplicationsPage() {
  const { t } = useI18n();
  return (
    <BusinessGate
      title={t("developers.pageTitle")}
      what={t("developers.pageTitle")}
    >
      <Apps />
    </BusinessGate>
  );
}

const IPV4 =
  /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}(\/([12]?\d|3[0-2]))?$/;
const IPV6 = /^[0-9a-f:]+(\/\d{1,3})?$/i;
const isIp = (s: string) => IPV4.test(s) || (s.includes(":") && IPV6.test(s));

const AVAILABLE_SCOPES = [
  "payments.read",
  "payments.write",
  "transactions.read",
  "wallets.read",
  "withdrawals.read",
  "withdrawals.write",
  "refunds.read",
  "refunds.write",
  "payment_links.read",
  "payment_links.write",
  "webhooks.read",
  "webhooks.write",
  "virtual_cards.users.read",
  "virtual_cards.users.write",
  "virtual_cards.cards.read",
  "virtual_cards.cards.sensitive.read",
  "virtual_cards.cards.write",
  "virtual_cards.cards.manage",
] as const;

const DEFAULT_SCOPES = ["payments.read", "payments.write", "transactions.read"];

function Apps() {
  const { t } = useI18n();
  const list = useServer<unknown>("/applications");
  const apps = useMemo(() => rows(list.data), [list.data]);
  const [sel, setSel] = useState<string | null>(null);
  const [create, setCreate] = useState(false);
  const selected = sel ?? text(apps[0], "id") ?? null;

  return (
    <>
      <PageHead title={t("developers.pageTitle")} sub={t("developers.pageSub")}>
        <Button variant="outline" asChild>
          <a
            href="https://docs.bizconnectpay.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("developers.docsBtn")}
          </a>
        </Button>
        <Button
          onClick={() => setCreate(true)}
          className="gap-1.5"
          data-testid="button-new-app"
        >
          <Plus className="h-4 w-4" />
          {t("developers.newAppBtn")}
        </Button>
      </PageHead>
      <div className="grid gap-5 lg:grid-cols-[300px_1fr]">
        <Panel className="p-2">
          {list.error ? (
            <ErrorBox error={list.error} onRetry={list.reload} />
          ) : list.loading ? (
            <SkeletonRows n={3} />
          ) : apps.length === 0 ? (
            <Empty
              icon={<AppWindow className="h-5 w-5" />}
              title={t("developers.emptyApps")}
            >
              {t("developers.emptyAppsSub")}
            </Empty>
          ) : (
            <ul className="space-y-1">
              {apps.map((a) => (
                <li key={text(a, "id")}>
                  <button
                    onClick={() => setSel(text(a, "id"))}
                    data-testid={`row-app-${text(a, "id")}`}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left transition",
                      selected === text(a, "id")
                        ? "bg-accent ring-1 ring-primary/20"
                        : "hover:bg-accent/50",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">
                        {text(a, "name")}
                      </p>
                      <p className="truncate text-[11px] text-muted-foreground">
                        {text(a, "description")}
                      </p>
                    </div>
                    <Tag value={text(a, "environment") || "SANDBOX"} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        {selected ? (
          <AppDetail
            key={selected}
            id={selected}
            onChanged={list.reload}
            onDeleted={() => {
              setSel(null);
              list.reload();
            }}
          />
        ) : null}
      </div>
      <CreateApp
        open={create}
        onClose={() => setCreate(false)}
        onCreated={(id) => {
          list.reload();
          setSel(id);
        }}
      />
    </>
  );
}

function CreateApp({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { t, lang } = useI18n();
  const [name, setName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [description, setDescription] = useState("");
  const [feePayer, setFeePayer] = useState<"MERCHANT" | "CUSTOMER">("MERCHANT");
  const [scopes, setScopes] = useState<string[]>(DEFAULT_SCOPES);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => {
    if (open) {
      setName("");
      setWebsiteUrl("");
      setDescription("");
      setFeePayer("MERCHANT");
      setScopes(DEFAULT_SCOPES);
      setErr(null);
    }
  }, [open]);
  async function submit(e: FormEvent) {
    e.preventDefault();
    const normalizedWebsiteUrl = websiteUrl.trim();
    try {
      const parsedWebsiteUrl = new URL(normalizedWebsiteUrl);
      if (!["http:", "https:"].includes(parsedWebsiteUrl.protocol)) {
        throw new Error("invalid");
      }
    } catch {
      setErr(
        new Error(
          lang === "en"
            ? "Enter a valid website URL starting with https://."
            : "Saisissez une URL de site valide commençant par https://.",
        ),
      );
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const { data } = await publicApi<ApiRecord>("/applications", {
        method: "POST",
        body: {
          name: name.trim(),
          website_url: normalizedWebsiteUrl,
          description: description.trim(),
          environment: "SANDBOX",
          fee_payer: feePayer,
          scopes,
        },
      });
      onCreated(text(data, "id", "application.id"));
      onClose();
    } catch (x) {
      setErr(x);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>
              {lang === "en" ? "New application" : "Nouvelle application"}
            </DialogTitle>
            <DialogDescription>
              {lang === "en"
                ? "Starts in SANDBOX mode."
                : "Elle démarre en SANDBOX."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="app-name">{lang === "en" ? "Name" : "Nom"}</Label>
            <Input
              id="app-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Boutique Akwa"
              data-testid="input-app-name"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="app-website-url">
              {lang === "en" ? "Merchant website URL" : "URL du site marchand"}
              <span className="ml-1 text-destructive" aria-hidden="true">
                *
              </span>
            </Label>
            <Input
              id="app-website-url"
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://exemple.com"
              autoComplete="url"
              required
              data-testid="input-app-website-url"
            />
            <p className="text-xs text-muted-foreground">
              {lang === "en"
                ? "Use the public website associated with this application."
                : "Indiquez le site public associé à cette application."}
            </p>
          </div>
          <section className="grid gap-2 rounded-lg border p-3 sm:col-span-2">
            <div>
              <h3 className="text-sm font-semibold">
                {lang === "en"
                  ? "API permissions (scopes)"
                  : "Autorisations API (scopes)"}
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">
                {lang === "en"
                  ? "Choose the permissions this application needs. At least one scope is required. Payment access is preselected; sensitive card data is never selected by default."
                  : "Choisissez les autorisations nécessaires. Au moins un scope est requis. Les accès paiement sont présélectionnés ; les données sensibles de carte ne le sont jamais."}
              </p>
            </div>
            <div className="grid gap-1 sm:grid-cols-2">
              {AVAILABLE_SCOPES.map((scope) => {
                const checked = scopes.includes(scope);
                const sensitive =
                  scope === "virtual_cards.cards.sensitive.read";
                return (
                  <label
                    key={scope}
                    className={cn(
                      "flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-accent/50",
                      sensitive &&
                        "border border-amber-300 bg-amber-50/60 dark:bg-amber-950/20",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(event) =>
                        setScopes((current) =>
                          event.target.checked
                            ? [...current, scope]
                            : current.filter((item) => item !== scope),
                        )
                      }
                      className="mt-0.5 accent-primary"
                      data-testid={`checkbox-app-scope-${scope}`}
                    />
                    <span className="min-w-0">
                      <code className="break-all font-mono">{scope}</code>
                      {sensitive && (
                        <span className="mt-0.5 block text-amber-800 dark:text-amber-300">
                          {lang === "en"
                            ? "Sensitive: may expose PAN, CVV and PIN."
                            : "Sensible : peut exposer le PAN, le CVV et le PIN."}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
            {scopes.length === 0 && (
              <p className="text-xs text-destructive" role="alert">
                {lang === "en"
                  ? "Select at least one scope; without scopes, API requests are denied."
                  : "Sélectionnez au moins un scope ; sans scope, les requêtes API sont refusées."}
              </p>
            )}
          </section>
          <div className="grid gap-1.5">
            <Label htmlFor="app-desc">Description</Label>
            <Input
              id="app-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Intégration checkout"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="app-fee-payer">{t("settings.feePayerLabel")}</Label>
            <Select
              value={feePayer}
              onValueChange={(v) => setFeePayer(v as "MERCHANT" | "CUSTOMER")}
            >
              <SelectTrigger
                id="app-fee-payer"
                data-testid="select-app-fee-payer"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MERCHANT">
                  {t("settings.feePayerMerchantOption")}
                </SelectItem>
                <SelectItem value="CUSTOMER">
                  {t("settings.feePayerCustomerOption")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <ErrorBox error={err} />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={busy}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                busy ||
                name.trim().length < 2 ||
                !websiteUrl.trim() ||
                scopes.length === 0
              }
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {lang === "en" ? "Create" : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CopyClientIdButton({
  clientId,
  compact = false,
}: {
  clientId: string;
  compact?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const onCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!clientId) return;
    void navigator.clipboard?.writeText(clientId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className={cn(
        "gap-1.5 transition-colors",
        compact ? "h-6 px-2 text-[11px]" : "h-8 px-2.5 text-xs",
        copied &&
          "border-emerald-500 bg-emerald-50/50 text-emerald-600 dark:bg-emerald-950/20",
      )}
      onClick={onCopy}
      title={copied ? "Copié !" : "Copier le client_id"}
      aria-label={copied ? "Copié" : "Copier"}
      data-testid="button-copy-client-id"
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5 text-emerald-600" />
          <span>Copié !</span>
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5 text-muted-foreground" />
          <span>{compact ? "Copier" : "Copier le client_id"}</span>
        </>
      )}
    </Button>
  );
}

function AppDetail({
  id,
  onChanged,
  onDeleted,
}: {
  id: string;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const { t, lang } = useI18n();
  const res = useServer<unknown>(`/applications/${encodeURIComponent(id)}`);
  const app = record(record(res.data).application ?? res.data);
  const creds = rows(app.credentials);
  const activeCred = creds.find((c) => text(c, "status") === "ACTIVE");
  const env = (text(app, "environment") || "SANDBOX").toUpperCase();
  const currentFeePayer =
    text(app, "fee_payer").toUpperCase() === "CUSTOMER"
      ? "CUSTOMER"
      : "MERCHANT";
  const [feePayer, setFeePayer] = useState<"MERCHANT" | "CUSTOMER">(
    currentFeePayer,
  );
  const [secret, setSecret] = useState<{
    clientId: string;
    secret: string;
  } | null>(null);
  const [expires, setExpires] = useState("");
  const [ips, setIps] = useState("");
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [tab, setTab] = useState("general");
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [confirm, setConfirm] = useState<
    | { kind: "rotate"; id: string }
    | { kind: "env"; to: string }
    | { kind: "delete" }
    | null
  >(null);
  useEffect(() => {
    const list =
      app.ip_allowlist ??
      app.ip_addresses ??
      record(app.ip_allowlist).ip_addresses;
    setIps(Array.isArray(list) ? (list as unknown[]).join("\n") : "");
    setFeePayer(
      text(app, "fee_payer").toUpperCase() === "CUSTOMER"
        ? "CUSTOMER"
        : "MERCHANT",
    );
    setSelectedScopes(
      rows(app.scopes)
        .map((scope) => text(scope, "code"))
        .filter(Boolean) as string[],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [res.data]);
  useEffect(() => () => setSecret(null), []);
  const ipList = ips
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const badIps = ipList.filter((s) => !isIp(s));

  async function run(key: string, fn: () => Promise<void>) {
    setBusy(key);
    setErr(null);
    try {
      await fn();
    } catch (x) {
      setErr(x);
    } finally {
      setBusy(null);
    }
  }
  const issue = () =>
    run("cred", async () => {
      const { data } = await publicApi<ApiRecord>(
        `/applications/${encodeURIComponent(id)}/credentials`,
        {
          method: "POST",
          body: expires ? { expires_at: new Date(expires).toISOString() } : {},
        },
      );
      setSecret({
        clientId: text(data, "client_id", "credential.client_id"),
        secret: text(data, "client_secret", "credential.client_secret"),
      });
      res.reload();
    });
  const confirmAction = () =>
    run("confirm", async () => {
      if (!confirm) return;
      if (confirm.kind === "rotate") {
        const { data } = await publicApi<ApiRecord>(
          `/credentials/${encodeURIComponent(confirm.id)}/rotate`,
          { method: "POST", body: {} },
        );
        setSecret({
          clientId: text(data, "client_id", "credential.client_id"),
          secret: text(
            data,
            "client_secret",
            "credential.client_secret",
            "secret",
          ),
        });
      } else if (confirm.kind === "env") {
        await publicApi(`/applications/${encodeURIComponent(id)}/environment`, {
          method: "PUT",
          body: {
            environment: confirm.to,
            live_confirmation: confirm.to === "LIVE",
          },
        });
        onChanged();
      } else {
        await publicApi(`/applications/${encodeURIComponent(id)}`, {
          method: "DELETE",
        });
        setConfirm(null);
        onDeleted();
        return;
      }
      setConfirm(null);
      res.reload();
    });
  const saveIps = () =>
    run("ip", async () => {
      await publicApi(`/applications/${encodeURIComponent(id)}/ip-allowlist`, {
        method: "PUT",
        body: { ip_addresses: ipList, ip_allowlist: ipList },
      });
      res.reload();
    });
  const saveScopes = () =>
    run("scopes", async () => {
      await publicApi(`/applications/${encodeURIComponent(id)}/scopes`, {
        method: "PATCH",
        body: { scopes: selectedScopes },
      });
      onChanged();
      res.reload();
    });
  const saveFeePayer = (nextPayer: "MERCHANT" | "CUSTOMER") => {
    setFeePayer(nextPayer);
    void run("fee_payer", async () => {
      await publicApi(`/applications/${encodeURIComponent(id)}/fee-payer`, {
        method: "PUT",
        body: { fee_payer: nextPayer },
      });
      onChanged();
      res.reload();
    });
  };

  if (res.error)
    return (
      <Panel>
        <ErrorBox error={res.error} onRetry={res.reload} />
      </Panel>
    );
  if (res.loading)
    return (
      <Panel>
        <SkeletonRows n={4} />
      </Panel>
    );

  return (
    <div className="space-y-5">
      <Panel
        title={text(app, "name")}
        sub={text(app, "description")}
        action={
          <div className="flex items-center gap-2">
            <Tag value={text(app, "status") || env} />
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={text(app, "status") === "REVOKED"}
              onClick={() => setConfirm({ kind: "delete" })}
              data-testid="button-delete-application"
            >
              <Trash2 className="mr-1 h-4 w-4" />
              {lang === "en" ? "Delete" : "Supprimer"}
            </Button>
          </div>
        }
      >
        <p className="text-xs text-muted-foreground">
          {lang === "en"
            ? "Manage this application’s environment, API permissions, credentials, IP restrictions, and webhooks."
            : "Gérez l’environnement, les autorisations API, les identifiants, les restrictions IP et les webhooks de cette application."}
        </p>
      </Panel>
      <div
        className="flex gap-1 overflow-x-auto border-b"
        role="tablist"
        aria-label={
          lang === "en" ? "Application settings" : "Paramètres de l’application"
        }
      >
        {[
          ["general", lang === "en" ? "General" : "Général"],
          ["scopes", "Scopes"],
          [
            "credentials",
            lang === "en" ? "API credentials" : "Identifiants API",
          ],
          ["ip", "IP"],
          ["webhooks", "Webhooks"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={cn(
              "shrink-0 border-b-2 px-3 py-2 text-sm",
              tab === key
                ? "border-primary font-semibold text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "general" && (
        <Panel
          title={
            lang === "en"
              ? "Application configuration"
              : "Configuration de l’application"
          }
          sub={
            lang === "en"
              ? "Environment and fee handling."
              : "Environnement et gestion des frais."
          }
          action={<Tag value={env} />}
        >
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-accent/50 p-3 text-xs">
            <span className="flex-1">
              {env === "LIVE"
                ? "Les requêtes éligibles sont routées vers les fournisseurs réels."
                : "Sandbox : résultats simulés, aucun mouvement de fonds réel."}
            </span>
            <Button
              size="sm"
              variant={env === "LIVE" ? "outline" : "default"}
              onClick={() => {
                setErr(null);
                setConfirm({
                  kind: "env",
                  to: env === "LIVE" ? "SANDBOX" : "LIVE",
                });
              }}
              data-testid="button-switch-env"
            >
              {lang === "en"
                ? `Switch to ${env === "LIVE" ? "SANDBOX" : "LIVE"}`
                : `Passer en ${env === "LIVE" ? "SANDBOX" : "LIVE"}`}
            </Button>
          </div>
          {text(app, "website_url") ? (
            <div className="mt-4 grid gap-1 border-t pt-4 text-sm">
              <span className="text-muted-foreground">
                {lang === "en" ? "Merchant website" : "Site marchand"}
              </span>
              <a
                href={text(app, "website_url")}
                target="_blank"
                rel="noopener noreferrer"
                className="break-all font-medium text-primary underline-offset-4 hover:underline"
              >
                {text(app, "website_url")}
              </a>
            </div>
          ) : null}
          <div className="mt-4 grid gap-2 border-t pt-4">
            <Label htmlFor="detail-fee-payer">
              {t("settings.feePayerLabel")}
            </Label>
            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={feePayer}
                onValueChange={(v) =>
                  saveFeePayer(v as "MERCHANT" | "CUSTOMER")
                }
                disabled={busy === "fee_payer"}
              >
                <SelectTrigger
                  id="detail-fee-payer"
                  className="max-w-md"
                  data-testid="select-detail-fee-payer"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MERCHANT">
                    {t("settings.feePayerMerchantOption")}
                  </SelectItem>
                  <SelectItem value="CUSTOMER">
                    {t("settings.feePayerCustomerOption")}
                  </SelectItem>
                </SelectContent>
              </Select>
              {busy === "fee_payer" && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {feePayer === "CUSTOMER"
                ? lang === "en"
                  ? "When the customer pays the fee, API payment requests (/v1/payments) automatically add the collection fee to the charged amount so your wallet receives 100% of the requested amount."
                  : "Lorsque le client paie les frais, les requêtes d'encaissement API (/v1/payments) ajoutent automatiquement les frais au montant débité au client afin que votre wallet reçoive 100 % du montant demandé."
                : lang === "en"
                  ? "The customer pays the exact requested amount and the collection fee is deducted before crediting your wallet."
                  : "Le client paie le montant exact demandé et les frais d'encaissement sont déduits avant de créditer votre wallet."}
            </p>
          </div>
          <ErrorBox className="mt-3" error={err} />
        </Panel>
      )}
      {tab === "scopes" && (
        <Panel
          title={lang === "en" ? "API scopes" : "Scopes API"}
          sub={
            lang === "en"
              ? "Permissions granted to this application’s credentials."
              : "Autorisations accordées aux identifiants de cette application."
          }
        >
          <div
            className="grid gap-1 sm:grid-cols-2"
            data-testid="app-scope-list"
          >
            {AVAILABLE_SCOPES.map((scope) => (
              <label
                key={scope}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-accent/50"
              >
                <input
                  type="checkbox"
                  checked={selectedScopes.includes(scope)}
                  onChange={(event) =>
                    setSelectedScopes((current) =>
                      event.target.checked
                        ? [...current, scope]
                        : current.filter((item) => item !== scope),
                    )
                  }
                  className="accent-primary"
                />
                <code className="break-all font-mono">{scope}</code>
              </label>
            ))}
          </div>
          {selectedScopes.length === 0 && (
            <p className="text-sm text-destructive">
              {lang === "en"
                ? "Select at least one scope."
                : "Sélectionnez au moins un scope."}
            </p>
          )}
          <Button
            size="sm"
            className="mt-3"
            onClick={() => void saveScopes()}
            disabled={busy === "scopes" || selectedScopes.length === 0}
          >
            {busy === "scopes" && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {lang === "en" ? "Save scopes" : "Enregistrer les scopes"}
          </Button>
          <ErrorBox className="mt-3" error={err} />
        </Panel>
      )}
      {tab === "credentials" && (
        <Panel
          title="Identifiants Basic"
          sub="client_id et client_secret à utiliser uniquement côté serveur. Le secret n'est affiché qu'une fois."
        >
          {activeCred && (
            <div
              className="mb-4 rounded-lg border border-primary/20 bg-primary/5 p-3.5"
              data-testid="active-credential-card"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-primary">
                    Identifiant actif (client_id)
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Identifiant pour vos requêtes API (Basic Auth et échange de
                    token).
                  </p>
                </div>
                <Tag value="ACTIVE" />
              </div>
              <div className="mt-2.5 flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded border bg-card px-2.5 py-1.5 font-mono text-xs font-semibold">
                  {text(activeCred, "client_id")}
                </code>
                <CopyClientIdButton
                  clientId={text(activeCred, "client_id") ?? ""}
                />
              </div>
            </div>
          )}
          {secret && (
            <div className="mb-4 space-y-2">
              <p className="text-xs">
                client_id : <code className="font-mono">{secret.clientId}</code>
              </p>
              {secret.secret && (
                <SecretOnce
                  label="client_secret"
                  value={secret.secret}
                  onClear={() => setSecret(null)}
                />
              )}
              <Button size="sm" variant="ghost" onClick={() => setSecret(null)}>
                J'ai enregistré le secret
              </Button>
            </div>
          )}
          {creds.length > 0 && (
            <ul className="mb-4 divide-y rounded-lg border">
              {creds.map((c) => (
                <li
                  key={text(c, "id")}
                  className="flex flex-wrap items-center gap-2 px-3 py-2 text-xs"
                >
                  <KeyRound className="h-4 w-4 text-muted-foreground" />
                  <code className="flex-1 truncate font-mono">
                    {text(c, "client_id")}
                  </code>
                  <CopyClientIdButton
                    clientId={text(c, "client_id") ?? ""}
                    compact
                  />
                  <span className="text-muted-foreground">
                    expire {fmtWhen(c.expires_at)}
                  </span>
                  <Tag value={text(c, "status")} />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7"
                    onClick={() => {
                      setErr(null);
                      setConfirm({ kind: "rotate", id: text(c, "id") });
                    }}
                  >
                    <RotateCw className="mr-1 h-3.5 w-3.5" />
                    Renouveler
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="grid gap-1.5">
              <Label htmlFor="cred-exp">Expiration (optionnelle)</Label>
              <Input
                id="cred-exp"
                type="datetime-local"
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
              />
            </div>
            <Button
              onClick={() => void issue()}
              disabled={busy === "cred"}
              data-testid="button-issue-credential"
            >
              {busy === "cred" && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Générer des identifiants
            </Button>
          </div>
        </Panel>
      )}
      {tab === "ip" && (
        <Panel
          title="Liste d'adresses IP autorisées"
          sub="Une adresse IPv4/IPv6 ou un CIDR par ligne. Vide = aucune restriction selon la politique serveur."
        >
          <textarea
            value={ips}
            onChange={(e) => setIps(e.target.value)}
            rows={4}
            aria-label="Adresses IP autorisées"
            className="w-full rounded-md border bg-card px-3 py-2 font-mono text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder="203.0.113.10"
            data-testid="input-ip-allowlist"
          />
          {badIps.length > 0 && (
            <p className="text-xs text-destructive">
              Adresses invalides : {badIps.join(", ")}
            </p>
          )}
          <Button
            size="sm"
            className="mt-2"
            onClick={() => void saveIps()}
            disabled={busy === "ip" || badIps.length > 0}
          >
            {busy === "ip" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enregistrer
          </Button>
        </Panel>
      )}
      {tab === "webhooks" && <WebhooksPanel applicationId={id} />}
      <ConfirmDialog
        open={Boolean(confirm)}
        destructive={
          confirm?.kind === "delete" ||
          confirm?.kind === "rotate" ||
          (confirm?.kind === "env" && confirm.to === "LIVE")
        }
        title={
          confirm?.kind === "delete"
            ? lang === "en"
              ? "Delete this application?"
              : "Supprimer cette application ?"
            : confirm?.kind === "rotate"
              ? "Renouveler ce secret ?"
              : `Passer en ${confirm?.kind === "env" ? confirm.to : ""} ?`
        }
        description={
          confirm?.kind === "delete"
            ? lang === "en"
              ? "The application will be revoked, credentials disabled and webhooks paused. Financial records will be retained for audit."
              : "L’application sera révoquée, ses identifiants désactivés et ses webhooks suspendus. Les données financières seront conservées pour audit."
            : confirm?.kind === "rotate"
              ? "L'ancien secret cessera de fonctionner. Le nouveau ne sera affiché qu'une fois."
              : confirm?.kind === "env" && confirm.to === "LIVE"
                ? "En LIVE, les requêtes déplacent des fonds réels. Le serveur refusera si l'entreprise n'est pas active et approuvée."
                : "Les requêtes seront simulées."
        }
        busy={busy === "confirm"}
        error={err}
        onClose={() => setConfirm(null)}
        onConfirm={() => void confirmAction()}
      />
    </div>
  );
}
